import { Injectable } from '@angular/core'
import { BehaviorSubject, fromEvent, Observable, Subscription, tap } from 'rxjs'
import { filter, map } from 'rxjs/operators'

@Injectable({ providedIn: 'root' })
export class ScrollService {
  private _containers: {
    [key: string]: {
      element: HTMLElement
      eventHandler: Observable<[number, number]>
      subscription: Subscription
    }
  } = {}

  private events: BehaviorSubject<{ [key: string]: [number, number] }> =
    new BehaviorSubject({})

  /**
   * Save the provided container
   * @param {string} name
   * @param {HTMLElement} element
   */
  public registerContainer(name: string, element: HTMLElement): void {
    if (this._containers[name]?.element === element) {
      return
    }
    this._containers[name]?.subscription.unsubscribe()
    const eventHandler = fromEvent(element, 'scroll').pipe(
      filter((event) => !!event.target),
      map(
        (event) =>
          [
            (event.target as any)['scrollLeft'],
            (event.target as any)['scrollTop'],
          ] as [number, number]
      )
    )
    const subscription = eventHandler
      .pipe(
        tap((event) => {
          const value = this.events.value
          value[name] = event
          this.events.next(value)
        })
      )
      .subscribe()
    this._containers[name] = { element, eventHandler, subscription }
  }

  /**
   * Forgets the provided container: stops listening to it and drops the
   * reference to its element, so a destroyed page is not retained by this
   * root service (SUP-1183)
   * @param {string} name
   */
  public unregisterContainer(name: string): void {
    this._containers[name]?.subscription.unsubscribe()
    delete this._containers[name]
    if (name in this.events.value) {
      const value = { ...this.events.value }
      delete value[name]
      this.events.next(value)
    }
  }

  /**
   *
   * @param {string} name
   * @param {[number, number]} tuple x, y to scroll to
   */
  public scrollTo(name: string, tuple: [number, number]): void {
    const container = this._containers[name]
    if (container) {
      container.element.scrollTo(tuple[0], tuple[1])
    }
  }

  /**
   * Will return the scroll observable
   * @param name
   */
  public scrollPosition(name: string): Observable<[number, number]> {
    return this.events.pipe(map((value) => value[name]))
  }

  /**
   * Will tell if a container is registered, with this element when one is
   * provided
   * @param name
   * @param element
   */
  public isContainerRegistered(name: string, element?: HTMLElement): boolean {
    const container = this._containers[name]
    return !!container && (!element || container.element === element)
  }
}
